import { createFileRoute, redirect, useLoaderData, useRouter } from '@tanstack/react-router'
import { SidebarTrigger } from '@repo/ui'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  cancelConversationChatExecution,
  correctConversationChatBrief,
  executeConversationChatBrief,
  getConversationChat,
  getConversationChatBrief,
  getConversationChatOutputStaleness,
  listConversationChats,
  redraftConversationChatOutput,
  retryConversationChatExecution,
  submitConversationChatCommand
} from '@/api/conversation'
import { deleteDocument, exportDocument, getDocument, renameDocument } from '@/api/documents/documents'
import { fetchSourceSummaries } from '@/api/content/refs'
import {
  cancelPresentationBuildRun,
  deselectPresentationSlideVersion,
  exportPresentation,
  getLatestPresentationBuildRun,
  getPresentation,
  getPresentationBuildRun,
  getPresentationMessages,
  getPresentationSlideMessages,
  refinePresentationDeck,
  refinePresentationSlide,
  renamePresentation,
  retryPresentationBuildRun,
  selectPresentationSlideVersion,
  startPresentationBuildRun,
  updatePresentationSlide
} from '@/api/presentations/presentations'
import {
  cancelProposalBuildRun,
  createSection,
  deleteProposal,
  deleteSection,
  deselectSectionVersion,
  editSection,
  exportProposal,
  getLatestProposalBuildRun,
  getProposal,
  getProposalBuildRun,
  getSectionMessages,
  renameProposal,
  renameSection,
  reorderSections,
  retryProposalBuildRun,
  selectSectionVersion,
  startProposalBuildRun
} from '@/api/proposals/proposals'
import { ConversationShell } from '@/features/conversation/ConversationShell'
import { useConversationSuggestions } from '@/features/conversation/suggestions'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'
import { useConversationController } from '@/features/conversation/controller'
import { useDraftConversationController } from '@/features/conversation/draft'
import {
  createConversationTag,
  loadConversationDocuments,
  loadConversationTags,
  uploadConversationDocument
} from '@/features/conversation/options'
import { WorkspaceCanvas, workspaceCollectionLoader } from '@/features/conversation/ConversationPanel'
import { useConversationWorkspaceShellController } from '@/features/shell/ConversationWorkspaceShell'
import { useScopedSettings } from '@/features/settings/ScopedSettings'
import type { SettingsTarget } from '@/features/settings/ScopedSettings'
import type { ConversationPanelServices } from '@/features/conversation/ConversationPanel'
import type { CanvasProvenance } from '@/features/conversation/canvas/PanelChrome'
import type { ContentPanelTarget } from '@/features/conversation/canvas/ContentPanels'
import type { ChatInspectorIdentity, WorkspaceCanvasIdentity } from '@/features/conversation/ConversationShell'
import type { ConversationChat } from '@/api/conversation'
import {
  canvasIdentity,
  canvasSearch,
  canvasSearchKeys,
  draftChat,
  inspectorIdentity,
  inspectorSearch,
  inspectorSearchKeys,
  switchChatSearch,
  workspaceSearchSchema
} from './-search'

function positiveRouteID(value: string, label: string): number {
  const parsed = Number(value)
  if (!Number.isSafeInteger(parsed) || parsed <= 0) throw new Error(`${label} must be a positive integer`)
  return parsed
}

// The server titles an automatic chat from its first task, and only while the title
// is still this placeholder, so the same pair of conditions decides when the client
// has to re-read it. See SetConversationChatAutomaticTitle.
const untitledChatPlaceholder = 'New chat'

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/workspace/')({
  validateSearch: workspaceSearchSchema,
  loaderDeps: ({ search }) => ({ chat: search.chat }),
  // The open chat is search state now, so it has to be a loader dependency: without
  // this the loader keeps the first chat's data and switching chats changes the URL
  // while the transcript stays where it was.
  beforeLoad: async ({ context, params, search }) => {
    if (!isConversationWorkspaceAvailable(context.user.userOrgProfile?.enabled_features)) {
      throw redirect({
        to: '/$orgSlug/$projectId',
        params: { orgSlug: params.orgSlug, projectId: params.projectId }
      })
    }
    // A workspace with nothing open resolves to the most recent chat, and a workspace
    // with no chats at all to a draft: that is the first-run surface, and it writes
    // nothing until the reader sends something into it.
    if (search.chat !== undefined || search.doc !== undefined) return
    const projectId = positiveRouteID(params.projectId, 'projectId')
    const page = await listConversationChats(projectId, { limit: 1 })
    throw redirect({
      to: '/$orgSlug/$projectId/workspace',
      params: { orgSlug: params.orgSlug, projectId: params.projectId },
      search: { ...search, chat: page.items[0]?.conversationId ?? draftChat }
    })
  },
  loader: ({ params, deps }) => {
    // A draft has nothing to load: the chat it will become does not exist yet.
    if (deps.chat === undefined || deps.chat === draftChat) return Promise.resolve(undefined)
    return getConversationChat(positiveRouteID(params.projectId, 'projectId'), deps.chat)
  },
  // A draft has no loader data to take a title from, but it does have a name on screen,
  // and a tab reading "Workspace" while the header reads "New chat" names two things.
  head: ({ loaderData, match }) => ({
    meta: [
      { title: `${loaderData?.title ?? (match.search.chat === draftChat ? 'New chat' : 'Workspace')} - Snowvault` }
    ]
  }),
  component: WorkspaceRouteComponent
})

export function WorkspaceRouteComponent() {
  const { orgSlug, projectId } = Route.useParams()
  const { userOrgProfile } = Route.useRouteContext().user
  const search = Route.useSearch()
  const chat = Route.useLoaderData()
  const numericProjectID = positiveRouteID(projectId, 'projectId')
  const canvas = canvasIdentity(search)
  const openInspectorIdentity = inspectorIdentity(search)
  const [wide, setWide] = useState(false)
  const canvasInvoker = useRef<HTMLElement | null>(null)
  const navigate = Route.useNavigate()
  // Switching chat keeps the canvas open, exactly as the rail's own chat switch does: the
  // document belongs to the workspace and a chat switch has no business closing it.
  //
  // Focus has to be placed rather than left. The control that sends the reader here is the
  // provenance line, whose whole condition is that the origin is not the open chat, so it
  // unmounts the instant the switch lands and focus would fall to the body every time the
  // button works. The chat's heading is where they asked to go; the composer would be too
  // much, opening a keyboard nobody asked for.
  const canvasProvenance = useCanvasProvenance(canvas, search.chat, (conversationId) => {
    void navigate({ search: (current) => switchChatSearch(current, conversationId) }).then(() =>
      window.requestAnimationFrame(() => document.getElementById('conversation-title')?.focus())
    )
  })
  // Above WorkspaceChat, which is keyed by the open chat: the corpus depends on the
  // workspace alone, so reading it inside a chat-keyed component would re-read the same
  // list on every switch.
  const suggestions = useConversationSuggestions(numericProjectID)
  const { openSettings } = useScopedSettings()
  const openCanvas = (identity: WorkspaceCanvasIdentity) => {
    if (
      document.activeElement instanceof HTMLElement &&
      document.activeElement.closest('[data-testid="workspace-canvas-slot"]') === null
    ) {
      canvasInvoker.current = document.activeElement
    }
    void navigate({ search: (current) => ({ ...omitKeys(current, canvasSearchKeys), ...canvasSearch(identity) }) })
  }
  const openContentPage = (target: ContentPanelTarget) => {
    openSettings(contentSettingsTarget(target, numericProjectID))
  }

  return (
    <div className="flex h-svh shrink-0 overflow-hidden">
      {search.chat === draftChat && (
        <WorkspaceDraftChat
          // Picker options and draft selections are route-scoped state. Remount when
          // either authority changes so a new workspace cannot inherit cached ids.
          key={`${orgSlug}:${numericProjectID}`}
          projectId={numericProjectID}
          collapsed={wide && canvas !== undefined}
          allowFloatingContext={canvas === undefined}
          suggestions={suggestions}
        />
      )}
      {chat === undefined ? null : (
        <WorkspaceChat
          key={chat.conversationId}
          projectId={numericProjectID}
          chat={chat}
          collapsed={wide && canvas !== undefined}
          allowFloatingContext={canvas === undefined}
          suggestions={suggestions}
          {...(openInspectorIdentity === undefined ? {} : { inspectorIdentity: openInspectorIdentity })}
          onOpenCanvas={openCanvas}
          onOpenInspector={(identity) =>
            void navigate({ search: (current) => ({ ...current, ...inspectorSearch(identity) }) })
          }
          onCloseInspector={() => void navigate({ search: (current) => omitKeys(current, inspectorSearchKeys) })}
        />
      )}
      <WorkspaceCanvas
        projectId={numericProjectID}
        projectName={useWorkspaceName(numericProjectID)}
        globalContentEnabled={userOrgProfile?.enabled_features.global_content ?? false}
        {...(canvas === undefined ? {} : { canvas })}
        {...(canvasProvenance === undefined ? {} : { provenance: canvasProvenance })}
        services={useWorkspaceCanvasServices(numericProjectID)}
        wide={wide}
        {...(chat === undefined && search.chat !== draftChat
          ? {}
          : { onToggleWide: () => setWide((current) => !current) })}
        onNavigate={openCanvas}
        onOpenContentPage={openContentPage}
        onClose={() => {
          setWide(false)
          void navigate({ search: (current) => omitKeys(current, canvasSearchKeys) })
        }}
        restoreFocus={() => canvasInvoker.current?.focus()}
      />
    </div>
  )
}

// Content stays in the new shell. The scope carried by each canvas address decides which
// settings dialog owns the item, while legacy content routes remain available when the
// conversation workspace feature is off.
export function contentSettingsTarget(target: ContentPanelTarget, projectId: number): SettingsTarget {
  const contentKind = target.page === 'text' ? 'text' : target.page === 'connector' ? 'connectors' : 'files'
  const contentItemId =
    target.page === 'file' || target.page === 'text'
      ? target.docId
      : target.page === 'connector'
        ? target.connectorId
        : undefined
  if ((target.page === 'file' || target.page === 'text') && target.scope === 'global') {
    return {
      scope: 'organisation',
      section: 'content',
      contentKind,
      ...(contentItemId !== undefined ? { contentItemId } : {})
    }
  }
  return {
    scope: 'workspace',
    projectId,
    section: 'content',
    contentKind,
    ...(contentItemId !== undefined ? { contentItemId } : {})
  }
}

function omitKeys<T extends Record<string, unknown>>(source: T, keys: ReadonlyArray<string>): T {
  const next = { ...source }
  for (const key of keys) delete next[key]
  return next
}

// The rail already fetched this workspace's artifacts and each carries where it
// started, so the canvas is handed that rather than asking again. An artifact outside
// the rail's window has no origin loaded and the header simply does not name one:
// absence is not a claim, as with the rail's activity dot.
//
// An origin that is the chat already open beside the canvas is dropped here rather than
// in the canvas. The line exists to explain an adjacency the reader did not expect, and
// a document sitting next to the chat that wrote it is the expected case.
function useCanvasProvenance(
  canvas: WorkspaceCanvasIdentity | undefined,
  openChat: number | typeof draftChat | undefined,
  openConversation: (conversationId: number) => void
): CanvasProvenance | undefined {
  const { shell } = useConversationWorkspaceShellController()
  if (canvas === undefined) return undefined
  // Only an artifact has an origin. A collection is a list rather than a thing a chat
  // wrote, and a content row belongs to the workspace's corpus however it got there, so
  // neither has a chat to name.
  if (canvas.canvas !== 'proposal' && canvas.canvas !== 'presentation' && canvas.canvas !== 'document') {
    return undefined
  }
  const artifactId =
    canvas.canvas === 'proposal'
      ? canvas.proposalId
      : canvas.canvas === 'document'
        ? canvas.documentId
        : canvas.presentationId
  const origin = shell?.expanded?.artifacts.items.find(
    (candidate) => candidate.kind === canvas.canvas && candidate.id === artifactId
  )?.origin
  if (origin === undefined || origin.conversationId === openChat) return undefined
  return { chatTitle: origin.title, onOpenChat: () => openConversation(origin.conversationId) }
}

function useWorkspaceName(projectId: number): string {
  const { allProjects } = useLoaderData({ from: '/_auth/$orgSlug' })
  return allProjects.find((candidate) => candidate.projectId === projectId)?.title ?? 'this workspace'
}

// The canvas reads workspace-owned artifacts, so its loaders are scoped to the workspace
// and to nothing else. They used to be built inside the chat route's memo, closed over a
// conversation id that had no bearing on which proposal loads. ADR-167.
function useWorkspaceCanvasServices(projectId: number): ConversationPanelServices {
  return useMemo<ConversationPanelServices>(
    () => ({
      ...unavailableChatServices,
      getProposal: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getProposal(projectId, proposalID)
      },
      getPresentation: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentation(projectId, presentationID)
      },
      getDocument: (requestedProjectID, documentID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getDocument(projectId, documentID)
      },
      exportProposal: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportProposal(projectId, proposalID)
      },
      exportPresentation: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportPresentation(projectId, presentationID)
      },
      exportDocument: (requestedProjectID, documentID, versionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportDocument(projectId, documentID, versionID)
      },
      getSectionMessages: (requestedProjectID, proposalID, sectionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getSectionMessages(projectId, proposalID, sectionID)
      },
      selectSectionVersion: (requestedProjectID, proposalID, sectionID, messageID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return selectSectionVersion(projectId, proposalID, sectionID, messageID)
      },
      deselectSectionVersion: (requestedProjectID, proposalID, sectionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deselectSectionVersion(projectId, proposalID, sectionID)
      },
      getPresentationSlideMessages: (requestedProjectID, presentationID, slideID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentationSlideMessages(projectId, presentationID, slideID)
      },
      selectPresentationSlideVersion: (requestedProjectID, presentationID, slideID, versionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return selectPresentationSlideVersion(projectId, presentationID, slideID, versionID)
      },
      deselectPresentationSlideVersion: (requestedProjectID, presentationID, slideID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deselectPresentationSlideVersion(projectId, presentationID, slideID)
      },
      // The artifact-scoped mutation surface. Like the reads above, these act on a
      // workspace artifact and name no chat, so the canvas binds them here; the
      // chat-inspector builder below omits them because the inspector renders no
      // artifact panels.
      createSection: (requestedProjectID, proposalID, heading) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return createSection(projectId, proposalID, heading)
      },
      renameSection: (requestedProjectID, proposalID, sectionID, heading) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return renameSection(projectId, proposalID, sectionID, heading)
      },
      deleteSection: (requestedProjectID, proposalID, sectionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deleteSection(projectId, proposalID, sectionID)
      },
      reorderSections: (requestedProjectID, proposalID, sectionIds) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return reorderSections(projectId, proposalID, sectionIds)
      },
      deleteProposal: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deleteProposal(projectId, proposalID)
      },
      renameProposal: (requestedProjectID, proposalID, documentName) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return renameProposal(projectId, proposalID, documentName)
      },
      editSection: (requestedProjectID, proposalID, sectionID, draftMarkdown) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return editSection(projectId, proposalID, sectionID, draftMarkdown)
      },
      startProposalBuildRun: (requestedProjectID, proposalID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return startProposalBuildRun(projectId, proposalID, request)
      },
      retryProposalBuildRun: (requestedProjectID, proposalID, runId) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return retryProposalBuildRun(projectId, proposalID, runId)
      },
      cancelProposalBuildRun: (requestedProjectID, proposalID, runId) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return cancelProposalBuildRun(projectId, proposalID, runId)
      },
      getLatestProposalBuildRun: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getLatestProposalBuildRun(projectId, proposalID)
      },
      getProposalBuildRun: (requestedProjectID, proposalID, runId, signal) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getProposalBuildRun(projectId, proposalID, runId, signal)
      },
      updatePresentationSlide: (requestedProjectID, presentationID, slideID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return updatePresentationSlide(projectId, presentationID, slideID, request)
      },
      refinePresentationSlide: (requestedProjectID, presentationID, slideID, instruction) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return refinePresentationSlide(projectId, presentationID, slideID, instruction)
      },
      refinePresentationDeck: (requestedProjectID, presentationID, instruction) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return refinePresentationDeck(projectId, presentationID, instruction)
      },
      renamePresentation: (requestedProjectID, presentationID, title) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return renamePresentation(projectId, presentationID, title)
      },
      startPresentationBuildRun: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return startPresentationBuildRun(projectId, presentationID)
      },
      retryPresentationBuildRun: (requestedProjectID, presentationID, runId) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return retryPresentationBuildRun(projectId, presentationID, runId)
      },
      cancelPresentationBuildRun: (requestedProjectID, presentationID, runId) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return cancelPresentationBuildRun(projectId, presentationID, runId)
      },
      getLatestPresentationBuildRun: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getLatestPresentationBuildRun(projectId, presentationID)
      },
      getPresentationBuildRun: (requestedProjectID, presentationID, runId, signal) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentationBuildRun(projectId, presentationID, runId, signal)
      },
      getPresentationMessages: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentationMessages(projectId, presentationID)
      },
      renameDocument: (requestedProjectID, documentID, title) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return renameDocument(projectId, documentID, title)
      },
      deleteDocument: (requestedProjectID, documentID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deleteDocument(projectId, documentID)
      },
      getSourceSummaries: fetchSourceSummaries,
      loadCollection: workspaceCollectionLoader(projectId),
      loadFactDocuments: (requestedProjectID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return loadConversationDocuments(projectId, request)
      }
    }),
    [projectId]
  )
}

// The canvas never calls these: they act on a chat's brief, output or execution, and the
// canvas has no chat. Rejecting is the honest implementation of "not reachable from
// here"; a silent no-op would let a future caller believe it worked.
const chatOnly = () => Promise.reject(new Error('This action needs an open chat.'))
const unavailableChatServices = {
  getBrief: chatOnly,
  correctBrief: chatOnly,
  executeBrief: chatOnly,
  getOutputStaleness: chatOnly,
  cancelExecution: chatOnly,
  retryExecution: chatOnly,
  redraftOutput: chatOnly
} satisfies Partial<ConversationPanelServices>

// The chat that does not exist yet. It is the same surface as a saved one, minus every
// action that needs a persisted chat to act on: there is nothing to rename, archive, or
// link to until the first message creates it. ADR-169.
function WorkspaceDraftChat({
  projectId,
  collapsed,
  allowFloatingContext,
  suggestions
}: {
  projectId: number
  collapsed: boolean
  allowFloatingContext: boolean
  suggestions: ReadonlyArray<string>
}) {
  const { userOrgProfile } = Route.useRouteContext().user
  const navigate = Route.useNavigate()
  const projectName = useWorkspaceName(projectId)
  const workspaceShellController = useConversationWorkspaceShellController()
  const { createChat, refreshChats } = workspaceShellController
  const services = useMemo(() => ({ createChat, submitCommand: submitConversationChatCommand }), [createChat])
  // The draft's address is replaced rather than pushed: Back should return to wherever
  // the reader started the chat from, not to an empty draft of the chat they now have.
  const onStarted = useCallback(
    (conversationId: number) => {
      void navigate({ replace: true, search: (current) => switchChatSearch(current, conversationId) })
      // The server titles the chat from this first task, and the rail row it created a
      // moment ago still reads "New chat" until it is asked again.
      void refreshChats()
    },
    [navigate, refreshChats]
  )
  const controller = useDraftConversationController(projectId, services, onStarted)

  return (
    <ConversationShell
      navigationControl={
        <SidebarTrigger className="size-11 shrink-0 md:hidden" aria-label="Open workspace navigation" />
      }
      projectId={projectId}
      chatTitle="New chat"
      projectName={projectName}
      currentUserId={userOrgProfile?.uid ?? 0}
      globalContentEnabled={userOrgProfile?.enabled_features.global_content ?? false}
      attachmentsEnabled={userOrgProfile?.enabled_features.file_attachments ?? false}
      controller={controller}
      collapsed={collapsed}
      allowFloatingContext={allowFloatingContext}
      suggestions={suggestions}
      // A draft is still the place where the first task is composed. Give it the same
      // source authority as a saved chat, including workspace pasted text.
      loadTags={loadConversationTags}
      createTag={createConversationTag}
      loadDocuments={(request) => loadConversationDocuments(projectId, request)}
      uploadDocument={(file) => uploadConversationDocument(projectId, file)}
    />
  )
}

function WorkspaceChat({
  projectId,
  chat,
  collapsed,
  allowFloatingContext,
  suggestions,
  inspectorIdentity: openInspectorIdentity,
  onOpenCanvas,
  onOpenInspector,
  onCloseInspector
}: {
  projectId: number
  chat: ConversationChat
  collapsed: boolean
  allowFloatingContext: boolean
  suggestions: ReadonlyArray<string>
  inspectorIdentity?: ChatInspectorIdentity
  onOpenCanvas: (identity: WorkspaceCanvasIdentity) => void
  onOpenInspector: (identity: ChatInspectorIdentity) => void
  onCloseInspector: () => void
}) {
  const conversationId = chat.conversationId
  const { userOrgProfile } = Route.useRouteContext().user
  const projectName = useWorkspaceName(projectId)
  const controller = useConversationController(projectId, conversationId)
  const workspaceShellController = useConversationWorkspaceShellController()
  const router = useRouter()
  const titleFollowsFirstTask = chat.titleMode === 'automatic' && chat.title === untitledChatPlaceholder
  const submit = useCallback<typeof controller.submit>(
    async (command) => {
      await controller.submit(command)
      if (command.type !== 'task' || !titleFollowsFirstTask) return
      // The header and document title come from the route loader, the rail row from
      // the shell query; neither hears about a title the server chose for itself.
      // The reload has to be synchronous: a background one recomputes `head` from
      // the loader data it is in the middle of replacing, leaving the browser tab
      // on the old title.
      await Promise.all([router.invalidate({ sync: true }), workspaceShellController.refreshChats()])
    },
    [controller, router, titleFollowsFirstTask, workspaceShellController]
  )
  const chatController = useMemo(() => ({ ...controller, submit }), [controller, submit])
  const archived = chat.archivedAtTsMs !== undefined
  const readOnly = archived || chat.readOnly
  const panelServices = useMemo<ConversationPanelServices>(
    () => ({
      getBrief: (requestedProjectID, briefID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getConversationChatBrief(projectId, conversationId, briefID)
      },
      correctBrief: (requestedProjectID, briefID, correction) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return correctConversationChatBrief(projectId, conversationId, briefID, correction)
      },
      executeBrief: (requestedProjectID, briefID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return executeConversationChatBrief(projectId, conversationId, briefID, request)
      },
      getOutputStaleness: (requestedProjectID, messageID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getConversationChatOutputStaleness(projectId, conversationId, messageID)
      },
      cancelExecution: (requestedProjectID, executionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return cancelConversationChatExecution(projectId, conversationId, executionID)
      },
      retryExecution: (requestedProjectID, executionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return retryConversationChatExecution(projectId, conversationId, executionID)
      },
      redraftOutput: (requestedProjectID, messageID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return redraftConversationChatOutput(projectId, conversationId, messageID, request)
      },
      getProposal: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getProposal(projectId, proposalID)
      },
      getPresentation: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentation(projectId, presentationID)
      },
      getDocument: (requestedProjectID, documentID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getDocument(projectId, documentID)
      },
      exportProposal: (requestedProjectID, proposalID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportProposal(projectId, proposalID)
      },
      exportPresentation: (requestedProjectID, presentationID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportPresentation(projectId, presentationID)
      },
      exportDocument: (requestedProjectID, documentID, versionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return exportDocument(projectId, documentID, versionID)
      },
      getSectionMessages: (requestedProjectID, proposalID, sectionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getSectionMessages(projectId, proposalID, sectionID)
      },
      selectSectionVersion: (requestedProjectID, proposalID, sectionID, messageID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return selectSectionVersion(projectId, proposalID, sectionID, messageID)
      },
      deselectSectionVersion: (requestedProjectID, proposalID, sectionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deselectSectionVersion(projectId, proposalID, sectionID)
      },
      getPresentationSlideMessages: (requestedProjectID, presentationID, slideID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return getPresentationSlideMessages(projectId, presentationID, slideID)
      },
      selectPresentationSlideVersion: (requestedProjectID, presentationID, slideID, versionID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return selectPresentationSlideVersion(projectId, presentationID, slideID, versionID)
      },
      deselectPresentationSlideVersion: (requestedProjectID, presentationID, slideID) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return deselectPresentationSlideVersion(projectId, presentationID, slideID)
      },
      getSourceSummaries: fetchSourceSummaries,
      loadFactDocuments: (requestedProjectID, request) => {
        if (requestedProjectID !== projectId) return Promise.reject(new Error('Workspace identity changed.'))
        return loadConversationDocuments(projectId, request)
      },
      loadCollection: workspaceCollectionLoader(projectId)
    }),
    [conversationId, projectId]
  )

  return (
    <ConversationShell
      navigationControl={
        <SidebarTrigger className="size-11 shrink-0 md:hidden" aria-label="Open workspace navigation" />
      }
      projectId={projectId}
      conversationId={conversationId}
      chatTitle={chat.title}
      projectName={projectName}
      currentUserId={userOrgProfile?.uid ?? 0}
      globalContentEnabled={userOrgProfile?.enabled_features.global_content ?? false}
      attachmentsEnabled={userOrgProfile?.enabled_features.file_attachments ?? false}
      controller={chatController}
      readOnly={readOnly}
      originKind={chat.originKind}
      cutoverReadOnly={chat.readOnly}
      collapsed={collapsed}
      allowFloatingContext={allowFloatingContext}
      {...(!readOnly
        ? {
            onRenameChat: async (title: string) => {
              const renamed = await workspaceShellController.renameChat(projectId, conversationId, title)
              if (renamed === undefined) throw new Error('Chat title could not be changed.')
            }
          }
        : {})}
      {
        // The compatibility home cannot be archived: the database forbids it while
        // legacy artifact writes still target it, so offering the action only ever
        // returned a conflict. Renaming it stays available above.
        ...(!readOnly && !chat.compatibilityHome
          ? {
              onArchiveChat: async () => {
                const archivedChat = await workspaceShellController.archiveChat(projectId, conversationId)
                if (archivedChat === undefined) throw new Error('Chat could not be archived.')
              }
            }
          : {})
      }
      {...(archived && !chat.readOnly
        ? {
            onRestoreChat: async () => {
              const restored = await workspaceShellController.restoreChat(projectId, conversationId)
              if (restored === undefined) throw new Error('Chat could not be restored.')
            }
          }
        : {})}
      onCopyLink={() => navigator.clipboard.writeText(window.location.href)}
      {...(openInspectorIdentity === undefined ? {} : { inspectorIdentity: openInspectorIdentity })}
      onOpenCanvas={onOpenCanvas}
      onOpenInspector={onOpenInspector}
      onCloseInspector={onCloseInspector}
      panelServices={panelServices}
      loadTags={loadConversationTags}
      createTag={createConversationTag}
      loadDocuments={(request) => loadConversationDocuments(projectId, request)}
      uploadDocument={(file) => uploadConversationDocument(projectId, file)}
      suggestions={suggestions}
    />
  )
}
